// §5a — splitting the roster by WHY impression share is being lost. PURE +
// tested.
//
// Lost-to-BUDGET and lost-to-RANK are two different conversations: "spend more"
// versus "fix bids and quality". Nothing on any page tells a Google specialist
// which one a client is in, and it is the single most actionable split the
// Google data can produce.
//
// ⚠️⚠️ THE THREE CONSTRAINTS FROM THE QUERY LAYER CARRY THROUGH TO HERE, and
// this file is where two of them become visible to a reader:
//
//  1. NULL MEANS NOT MEASURED, AND MUST RENDER "—" — NEVER 0%. Google writes
//     0.0 rather than NULL where a share cannot apply (measured: 170 of 252
//     SEARCH rows report 0% click share while carrying 1,719 clicks), so a
//     confident zero here would tell a PMax-heavy client they captured none of
//     an auction they were never in. Every ratio goes through
//     `sharePctOrNull`, the SAME helper `computeMetrics` uses.
//  2. CAMPAIGN GRAIN ONLY. Ad-group and ad rows carry no share at all
//     (`ads_AdGroupCrossDeviceStats` is not a transfer spec), so there is no
//     deeper split to offer and this never implies one.
//  3. The impression-WEIGHTED family (top / absolute-top / exact-match / click
//     share) is absent by construction — it has a different denominator Google
//     does not expose, and the server projection does not even select it.

import { sharePctOrNull } from '../googleAdsMetrics.js';

/** One Location's summed competitiveness numerators (the wire shape). */
export interface CompetitivenessAccountRow {
  accountId: string;
  searchImpressions: number;
  eligibleImpressions: number;
  budgetLostImpressions: number;
  rankLostImpressions: number;
}

/**
 * WHY the auction is being lost — the conversation this panel exists to start.
 *
 * ⚠️ `not_measured` is a FIRST-CLASS verdict, not an error and not a zero. An
 * account with no measurable search auction is a normal, common state (PMax and
 * Display carry no impression share), and collapsing it into `healthy` would
 * quietly claim we checked something we did not.
 */
export type ShareLossReason = 'budget' | 'rank' | 'healthy' | 'not_measured';

export interface ShareLossRow {
  accountId: string;
  /** Percent 0-100, or null where the auction was not measurable. */
  impressionShare: number | null;
  budgetLostShare: number | null;
  rankLostShare: number | null;
  reason: ShareLossReason;
}

/**
 * ⚠️ THE FLOOR IS A LEGIBILITY GUARD, NOT A CORRECTNESS ONE. An account losing
 * 4% to budget is not in a budget conversation, and a worklist that says it is
 * will be ignored within a week — the same reasoning as the alert core's volume
 * guards. Losing less than this to BOTH causes reads as `healthy`.
 */
export const SHARE_LOSS_FLOOR_PCT = 10;

/**
 * Which cause dominates.
 *
 * ⚠️ WHICHEVER IS LARGER WINS, and there is deliberately no "both" verdict.
 * The two are not independent — a budget-capped campaign stops bidding, which
 * depresses rank-based loss measurement — so presenting them as co-equal
 * diagnoses would invite a specialist to act on both at once when the budget
 * fix is what changes the rank picture. One conversation at a time, biggest
 * first; the raw percentages are on the row for anyone who wants to look.
 *
 * ⚠️ A NULL on either side is NOT treated as zero. If neither cause is
 * measurable the verdict is `not_measured`, because "we could not see it" and
 * "we saw it and it was fine" are different things to tell a specialist, and
 * only one of them means stop looking.
 */
export function classifyShareLoss(
  budgetLostShare: number | null,
  rankLostShare: number | null,
): ShareLossReason {
  if (budgetLostShare === null && rankLostShare === null) return 'not_measured';
  const budget = budgetLostShare ?? 0;
  const rank = rankLostShare ?? 0;
  if (budget < SHARE_LOSS_FLOOR_PCT && rank < SHARE_LOSS_FLOOR_PCT) return 'healthy';
  return budget >= rank ? 'budget' : 'rank';
}

/**
 * Turn summed numerators into the three ratios plus the verdict.
 *
 * ⚠️ THE RATIOS ARE RE-DERIVED FROM SUMMED NUMERATOR OVER SUMMED DENOMINATOR,
 * never averaged from per-campaign shares — the locked rule for every ratio in
 * this module, and the entire reason the derived `campaign_competitiveness`
 * view emits additive columns at all. Averaging would weight a campaign with 3
 * impressions the same as one with 3,000.
 *
 * ⚠️ ALL THREE SHARE ONE DENOMINATOR (`eligibleImpressions`), which is what
 * makes the identity (won + lost-budget + lost-rank ≈ 1) hold. Dividing any of
 * them by `searchImpressions` instead would be the crossed-denominator bug the
 * column naming exists to prevent — and it would look entirely reasonable.
 */
export function buildShareLossRow(row: CompetitivenessAccountRow): ShareLossRow {
  const eligible = row.eligibleImpressions;
  const impressionShare = sharePctOrNull(row.searchImpressions, eligible);
  const budgetLostShare = sharePctOrNull(row.budgetLostImpressions, eligible);
  const rankLostShare = sharePctOrNull(row.rankLostImpressions, eligible);
  return {
    accountId: row.accountId,
    impressionShare,
    budgetLostShare,
    rankLostShare,
    reason: classifyShareLoss(budgetLostShare, rankLostShare),
  };
}

/**
 * The worklist: the accounts actually losing the auction, worst first.
 *
 * ⚠️ `not_measured` accounts are RETURNED, not filtered out, and sorted LAST.
 * A Google specialist looking at a roster needs to know which clients they
 * cannot see rather than having them silently vanish — "no data" is itself a
 * thing to chase, and an account missing from a list is indistinguishable from
 * one that is fine.
 */
export function buildShareLossWorklist(
  rows: readonly CompetitivenessAccountRow[],
): ShareLossRow[] {
  const ORDER: Record<ShareLossReason, number> = {
    budget: 0,
    rank: 1,
    healthy: 2,
    not_measured: 3,
  };
  return rows
    .map(buildShareLossRow)
    .sort((a, b) => {
      const byReason = ORDER[a.reason] - ORDER[b.reason];
      if (byReason !== 0) return byReason;
      // Within a cause, the biggest loss first — that is the order a specialist
      // would work the list in anyway.
      const aLoss = Math.max(a.budgetLostShare ?? 0, a.rankLostShare ?? 0);
      const bLoss = Math.max(b.budgetLostShare ?? 0, b.rankLostShare ?? 0);
      return bLoss - aLoss;
    });
}

/** How many accounts sit in each verdict — the panel's summary line. */
export function countShareLossReasons(
  rows: readonly ShareLossRow[],
): Record<ShareLossReason, number> {
  const counts: Record<ShareLossReason, number> = {
    budget: 0,
    rank: 0,
    healthy: 0,
    not_measured: 0,
  };
  for (const r of rows) counts[r.reason] += 1;
  return counts;
}
